import { InfoCircleOutlined } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import { ChevronDown } from 'lucide-react';
import { useEffect } from 'react';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ToggleRadio from '@/components/Common/ToggleRadio';
import Menu from '@/components/Menu/Menu';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import {
  CHART_CALCULATION,
  CHART_METRIC,
  CHART_SHARE_CALCULATION_TYPE,
} from '@/entities/chart/model/constants';
import useChartStateAction from '@/features/(charts)/chart-controller/hooks/use-chart-state-action/useChartStateAction';
import { useChartMetricPolicy } from '@/features/(charts)/chart-metric-policy';
import {
  calculationEnabledMetricList,
  isViewTypeEnabled,
} from '@/features/(charts)/chart-renderer/lib/conditions';
import { ChartCalculationMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import type { FeedbackSentiment } from '@/types/feedbackConst';

import { CalculationSelect } from './CalculationSelect';
import { CellCalculationSelect } from './CellCalculationSelect';
import { ChartDimensionSelect } from './chart-dimension-select';
import { ViewTypeSelect } from './ViewTypeSelect';

type Props = {
  chartInfo: ChartContextProps;
  series: ChartSeriesDef;
  index: number;
  calculationTypeEnabled: boolean;
  cellCalculationSegmentEnabled: boolean;
  showRowTotalEnabled: boolean;
  onViewTypeChange: (index: number, e: RadioChangeEvent) => void;
  onShareCalculationTypeChange: (
    index: number,
    calculationType: CHART_SHARE_CALCULATION_TYPE,
  ) => void;
  onShareCellCalculationSegmentChange: (
    index: number,
    value: FeedbackSentiment,
  ) => void;
  onShowRowTotalChange: (value: boolean, index: number) => void;
};

export const SeriesItem = (props: Props) => {
  const {
    chartInfo,
    series,
    index,
    calculationTypeEnabled,
    cellCalculationSegmentEnabled,
    showRowTotalEnabled,
    onViewTypeChange,
    onShareCalculationTypeChange,
    onShareCellCalculationSegmentChange,
    onShowRowTotalChange,
  } = props;
  const { state } = chartInfo;
  const { onMetricChange, onCalculationChange } =
    useChartStateAction(chartInfo);
  const { metricOptions, getMetricLabel, getSafeMetric } =
    useChartMetricPolicy();
  const selectedMetric = getSafeMetric(series.measure.metric);

  useEffect(() => {
    if (series.measure.metric === selectedMetric) {
      return;
    }

    onMetricChange(selectedMetric, index);
  }, [index, onMetricChange, selectedMetric, series.measure.metric]);

  const metricMenuOptions = metricOptions.map((item) => ({
    label: item.label,
    onClick: () => onMetricChange(item.value, index),
  }));

  const availableCalculationOptions = ChartCalculationMap.toArray().filter(
    (item) => item.value !== CHART_CALCULATION.COUNT,
  );

  const calculationMenuOptions = availableCalculationOptions.map((item) => ({
    label: item.label,
    onClick: () => onCalculationChange(item.value, index),
  }));

  return (
    <div className="flex flex-col gap-3">
      <ChartDimensionSelect
        chartInfo={chartInfo}
        chartSeriesIndex={index}
        switchFlag={0}
      />

      <VFlex g={0}>
        <Flex g={8} a="center" j="space-between">
          <Flex g={4} a="center">
            <Typo type="label2" className="text-gray-600">
              Measured by
            </Typo>
            {selectedMetric === CHART_METRIC.IMPRESSION && (
              <TooltipV2
                tooltipNode={
                  <Typo type="body3">
                    Impressions are calculated differently by channel.
                    <br />
                    Instagram may use likes + comments, YouTube uses views, and
                    Reddit uses comments.
                  </Typo>
                }
              >
                <Typo className="text-gray-600" type="label2">
                  <InfoCircleOutlined />
                </Typo>
              </TooltipV2>
            )}
          </Flex>
          <Menu
            modalWidth={200}
            triggerNode={
              <Button
                variant="ghost"
                className="-mr-2"
                sizeType="m"
                rightIcon={<ChevronDown className="size-3 text-gray-600" />}
              >
                {getMetricLabel(selectedMetric)}
              </Button>
            }
            options={metricMenuOptions}
          />
        </Flex>
        {calculationEnabledMetricList.includes(selectedMetric) && (
          <Flex j="flex-end">
            <Menu
              modalWidth={200}
              triggerNode={
                <Button
                  className="-mr-2"
                  variant="ghost"
                  sizeType="m"
                  rightIcon={<ChevronDown className="size-3 text-gray-600" />}
                >
                  {ChartCalculationMap.get(series.measure.calculation)}
                </Button>
              }
              options={calculationMenuOptions}
            />
          </Flex>
        )}
      </VFlex>
      {isViewTypeEnabled(state.graph, selectedMetric) && (
        <>
          <ViewTypeSelect
            value={series.options?.view_type}
            onChange={(e) => onViewTypeChange(index, e)}
          />
          <VFlex>
            {showRowTotalEnabled && (
              <Flex j="flex-end" a="center" g={8}>
                <Typo
                  className="text-gray-700"
                  weight="m"
                  style={{ fontSize: 13, lineHeight: 1 }}
                >
                  Row total
                </Typo>
                <ToggleRadio
                  status={series.options?.show_row_total || false}
                  onChange={(value) => onShowRowTotalChange(value, index)}
                />
              </Flex>
            )}

            {calculationTypeEnabled && (
              <CalculationSelect
                value={
                  series.options?.share_calculation_type ||
                  CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL
                }
                showCellOption={selectedMetric !== CHART_METRIC.IMPRESSION}
                onChange={(value) => onShareCalculationTypeChange(index, value)}
              />
            )}
            {cellCalculationSegmentEnabled && (
              <CellCalculationSelect
                value={
                  series.options
                    ?.share_cell_calculation_segment as FeedbackSentiment
                }
                onChange={(value) =>
                  onShareCellCalculationSegmentChange(index, value)
                }
              />
            )}
          </VFlex>
        </>
      )}
    </div>
  );
};
